import { Link } from '@tanstack/react-router'
import { ArrowRight, Check } from 'lucide-react'

import { MaskedHeading } from '@/components/masked-heading'
import { PromoBanner, PromoSlideshow } from '@/components/promo'
import { Reveal } from '@/components/reveal'
import { Button } from '@/components/ui/button'
import { useCourses } from '@/features/catalog/api'
import { useServices } from '@/features/services/api'
import { useVacancies } from '@/features/hiring/api'
import { useContests } from '@/features/contest/api'
import { useCountUp, useScrollProgress } from '@/lib/motion'
import { formatRupiah, formatRupiahShort } from '@/lib/format'

export function HomePage() {
  return (
    <>
      <Hero />
      <Marquee />
      <Sorotan />
      <LiniMasaBand />
      <WorkGallery />
      <WordmarkBand />
      <MakeItYours />
      <PitaIklan />
      <BringYourTeam />
      <EscrowBand />
      <Plans />
      <ClosingCta />
    </>
  )
}

/**
 * Sorotan menayangkan slideshow yang diatur admin.
 *
 * Jarak atas dan bawahnya melekat pada slideshow itu sendiri, bukan pada
 * pembungkusnya, sehingga beranda tanpa slide aktif tidak menyisakan pita
 * kosong di antara dua bagian yang seharusnya berdampingan.
 */
function Sorotan() {
  return (
    <div className="bg-surface px-4 sm:px-6">
      <div className="mx-auto w-full max-w-7xl">
        <PromoSlideshow className="py-6 sm:py-10" />
      </div>
    </div>
  )
}

function PitaIklan() {
  return (
    <div className="bg-surface px-4 sm:px-6">
      <div className="mx-auto w-full max-w-7xl">
        <PromoBanner slot="beranda_banner" className="my-6 sm:my-10" />
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------
   Hero
   ------------------------------------------------------------------------- */

/**
 * Kartu foto yang berhamburan di sekeliling judul.
 *
 * dx dan dy adalah jarak tempuh kartu dari titik kumpulnya di tengah menuju
 * tempat mendaratnya. Nilai inilah yang dipakai dua arah: dilalui saat
 * halaman dibuka, dan dilalui mundur saat pengguna menggulir turun.
 */
const scatter = [
  { src: 'https://images.unsplash.com/photo-1580894732444-8ecded7900cd?w=520&q=80', className: 'left-[1.5%] top-[14%] w-[10.5rem]', tilt: '-6deg', dx: '30rem', dy: '11rem' },
  { src: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=380&q=80', className: 'left-[8%] top-[48%] w-[7.5rem]', tilt: '5deg', dx: '24rem', dy: '2rem' },
  { src: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=420&q=80', className: 'left-[1%] bottom-[8%] w-[9rem]', tilt: '-3deg', dx: '30rem', dy: '-9rem' },
  { src: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=420&q=80', className: 'right-[2%] top-[11%] w-[8rem]', tilt: '7deg', dx: '-30rem', dy: '12rem' },
  { src: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=560&q=80', className: 'right-[8.5%] top-[44%] w-[7.5rem]', tilt: '-5deg', dx: '-23rem', dy: '3rem' },
  { src: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=520&q=80', className: 'right-[1.5%] bottom-[7%] w-[10rem]', tilt: '4deg', dx: '-29rem', dy: '-10rem' },
] as const

function Hero() {
  const scatterRef = useScrollProgress<HTMLDivElement>()

  return (
    <section className="relative isolate -mt-19 overflow-hidden bg-page pt-19 sm:-mt-21 sm:pt-21">
      <div aria-hidden className="grid-lines pointer-events-none absolute inset-0" />

      {/* Kartu latar disembunyikan dari pembaca layar dan dari layar sempit:
          di lebar ponsel ia hanya akan menumpuki judulnya. */}
      <div ref={scatterRef} aria-hidden className="pointer-events-none absolute inset-0 hidden lg:block">
        {scatter.map((item) => (
          <div
            key={item.className}
            className={`card-hard scatter-item absolute ${item.className}`}
            style={{
              ['--tilt' as string]: item.tilt,
              ['--dx' as string]: item.dx,
              ['--dy' as string]: item.dy,
            }}
          >
            <img src={item.src} alt="" loading="lazy" className="block aspect-4/5 w-full object-cover" />
          </div>
        ))}
      </div>

      <div className="relative mx-auto w-full max-w-4xl px-4 py-20 text-center sm:px-6 lg:py-28">
        <p className="mono-label text-[11px] text-muted sm:text-xs">
          Jejaring kerja yang benar-benar membayar
        </p>

        <MaskedHeading
          as="h1"
          className="display mt-5 text-ink"
          lines={['Berjejaring.', 'Berkarya.', <span key="dibayar" className="invert-block">Dibayar.</span>]}
          step={90}
        />

        <p className="mx-auto mt-7 max-w-lg text-lg leading-snug text-muted">
          Lini masa untuk menunjukkan yang Anda kerjakan, grup untuk menemukan orangnya, dan escrow yang
          menjaga uangnya. Satu tempat — bukan tiga aplikasi.
        </p>

        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Link to="/daftar">
            <Button size="lg" variant="inverted">
              Buat akun
              <ArrowRight className="size-4" />
            </Button>
          </Link>
          <Link to="/kelas">
            <Button size="lg" variant="outline">
              Lihat kelas
            </Button>
          </Link>
        </div>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------
   Pita berjalan
   ------------------------------------------------------------------------- */

const marqueeWords = ['Lini masa', 'Grup', 'Kelas', 'Paket jasa', 'Proyek escrow', 'Sayembara', 'Lowongan', 'Talenta']

function Marquee() {
  // Isi digandakan persis dua kali; animasi menggeser tepat setengah lebar,
  // sehingga sambungannya tidak pernah terlihat.
  const items = [...marqueeWords, ...marqueeWords]

  return (
    <div className="overflow-hidden border-y-2 border-line-strong bg-surface py-4" aria-hidden>
      <div className="marquee-track items-center gap-8">
        {items.map((word, index) => (
          <span key={`${word}-${index}`} className="flex shrink-0 items-center gap-8">
            <span className="outline-text display-sm">{word}</span>
            <span className="size-2.5 shrink-0 rotate-45 bg-brand" />
          </span>
        ))}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------
   Sisi jejaring
   ------------------------------------------------------------------------- */

/**
 * LiniMasaBand menjelaskan sisi jejaring skava.id.
 *
 * Bagian ini ada karena itulah yang paling sering disalahpahami: orang
 * mengira ini papan lowongan biasa. Yang membedakannya adalah kiriman
 * sehari-hari — dan pekerjaan datang dari sana, bukan dari lamaran.
 */
function LiniMasaBand() {
  const kartu = [
    {
      judul: 'Lini masa',
      isi: 'Tunjukkan yang sedang Anda kerjakan hari ini. Bukan riwayat kerja lima tahun lalu — pekerjaan datang kepada orang yang terlihat sedang bekerja.',
      warna: 'bg-kelas',
    },
    {
      judul: 'Grup',
      isi: 'Berkumpul menurut bidang: Flutter, desain, data, apa pun. Terbuka untuk dibaca siapa saja, atau tertutup hanya untuk anggotanya.',
      warna: 'bg-jasa',
    },
    {
      judul: 'Ikuti orangnya',
      isi: 'Pantau orang yang cara kerjanya ingin Anda pelajari. Kirimannya masuk ke lini masa Anda tanpa perlu dicari ulang.',
      warna: 'bg-talenta',
    },
  ]

  return (
    <section className="border-y-2 border-line-strong bg-sunken px-4 py-20 sm:px-6 lg:py-28">
      <div className="mx-auto w-full max-w-7xl">
        <Reveal className="max-w-2xl">
          <p className="mono-label text-[11px] text-brand">Lini masa &amp; grup</p>
          <MaskedHeading
            className="display-light mt-4 text-ink"
            lines={['Profil yang diam', 'tidak pernah dapat kerja.']}
          />
          <p className="mt-5 text-lg leading-relaxed text-muted">
            Di sini Anda tidak sekadar punya halaman profil. Anda menulis, menunjukkan hasil kerja, dan
            berkumpul dengan orang sebidang — lalu kelas, proyek, dan lowongannya menyusul di tempat yang sama.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-3">
          {kartu.map((k, index) => (
            <Reveal key={k.judul} delay={index * 80}>
              <article className="card-hard hard-dark h-full bg-surface p-6">
                <span className={`mono-label inline-block rounded-full px-3 py-1 text-[10px] text-arang-900 ${k.warna}`}>
                  {k.judul}
                </span>
                <p className="mt-4 leading-relaxed text-muted">{k.isi}</p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={240} className="mt-10">
          <Link to="/daftar">
            <Button size="lg">
              Mulai menulis
              <ArrowRight className="size-4" />
            </Button>
          </Link>
        </Reveal>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------
   Galeri pekerjaan
   ------------------------------------------------------------------------- */

const galleryTilts = ['-2.5deg', '1.8deg', '-1.2deg', '2.4deg', '-1.8deg', '1.4deg']

function WorkGallery() {
  const { data: courseData } = useCourses({ sort: 'popular' })
  const { data: serviceData } = useServices({ sort: 'popular' })

  const items = [
    ...(courseData?.courses ?? []).slice(0, 3).map((course) => ({
      id: course.id,
      title: course.title,
      by: course.expert.full_name,
      image: course.thumbnail_url,
      to: '/kelas/$slug' as const,
      params: { slug: course.slug },
      tag: 'Kelas',
    })),
    ...(serviceData?.services ?? []).slice(0, 3).map((service) => ({
      id: service.id,
      title: service.title,
      by: service.seller.full_name,
      image: service.thumbnail_url,
      to: '/jasa/$slug' as const,
      params: { slug: service.slug },
      tag: 'Jasa',
    })),
  ]

  return (
    <section className="bg-surface px-4 py-20 sm:px-6 lg:py-28">
      <Reveal className="mx-auto max-w-3xl text-center">
        <MaskedHeading
          className="display-light text-ink"
          lines={['Orangnya dulu.', 'Pekerjaannya menyusul.']}
        />
        <div className="mt-8 flex justify-center">
          <Link to="/talenta">
            <Button size="lg">
              Lihat talenta
              <ArrowRight className="size-4" />
            </Button>
          </Link>
        </div>
      </Reveal>

      {items.length > 0 ? (
        <div className="mx-auto mt-16 grid w-full max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, index) => (
            <Reveal key={item.id} delay={index * 70}>
              <Link
                to={item.to}
                params={item.params}
                className="group block"
                style={{ ['--tilt' as string]: galleryTilts[index % galleryTilts.length] }}
              >
                <div className="card-hard tilt overflow-hidden transition duration-300 group-hover:rotate-0">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt=""
                      loading="lazy"
                      className="block aspect-16/10 w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex aspect-16/10 w-full items-center justify-center bg-brand-soft">
                      <span className="mono-label text-xs text-brand">{item.tag}</span>
                    </div>
                  )}
                </div>
                <div className="mt-3 px-1">
                  <p className="clamp-2 font-semibold text-ink">{item.title}</p>
                  <p className="mono-label mt-1 text-[11px] text-subtle">{item.by}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      ) : null}
    </section>
  )
}

/* -------------------------------------------------------------------------
   Pita nama
   ------------------------------------------------------------------------- */

function WordmarkBand() {
  // Angka di sini semuanya sifat produk yang dapat diperiksa sendiri di
  // dalam aplikasi, bukan capaian pemakaian. Menaruh jumlah pengguna di
  // halaman awal sebuah platform yang baru berjalan hanya akan menjadi
  // klaim yang tidak bisa dipertanggungjawabkan.
  const facts = [
    { value: 5, suffix: '', label: 'model kerja dalam satu akun' },
    { value: 10, suffix: '%', label: 'komisi, tampil sebelum Anda setuju' },
    { value: 100, suffix: '%', label: 'nominal beserta rinciannya' },
    { value: 0, suffix: '', label: 'rupiah berpindah tanpa jejak audit' },
  ]

  return (
    <section className="relative isolate overflow-hidden bg-arang-900 px-4 py-20 sm:px-6 lg:py-28">
      {/* Nama merek turun pangkat menjadi tekstur: berjalan pelan di latar
          dengan huruf berongga, cukup untuk memberi gerak tanpa merebut
          perhatian dari angkanya. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center opacity-[0.18]">
        <div className="marquee-track marquee-track-slow">
          {['SKAVA', 'SKAVA', 'SKAVA', 'SKAVA'].map((word, index) => (
            <span
              key={index}
              className="shrink-0 pr-8 font-bold leading-none tracking-[-0.05em] text-transparent"
              style={{ fontSize: 'clamp(6rem, 16vw, 13rem)', WebkitTextStroke: '2px #ffffff' }}
            >
              {word}
            </span>
          ))}
        </div>
      </div>

      <div className="relative mx-auto w-full max-w-6xl">
        <Reveal>
          <p className="mono-label text-[11px] text-white/70">Yang bisa Anda periksa sendiri</p>
        </Reveal>

        <MaskedHeading
          className="display-sm mt-4 max-w-3xl text-white"
          lines={['Bukan janji manis.', 'Angka yang ada di layar Anda.']}
        />

        <dl className="mt-14 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((fact, index) => (
            <Reveal key={fact.label} delay={index * 80}>
              <FactCell value={fact.value} suffix={fact.suffix} label={fact.label} />
            </Reveal>
          ))}
        </dl>
      </div>
    </section>
  )
}

function FactCell({ value, suffix, label }: { value: number; suffix: string; label: string }) {
  const ref = useCountUp(value)

  return (
    <div className="border-t-2 border-white/30 pt-5">
      <dd className="text-6xl font-bold tracking-[-0.05em] text-white">
        {/* Nilai akhirnya tetap tertulis di DOM sejak awal; yang beranimasi
            hanya teks yang terlihat, sehingga pembaca layar tidak pernah
            membacakan angka setengah jalan. */}
        <span ref={ref}>{value}</span>
        {suffix}
      </dd>
      <dt className="mt-3 max-w-[16rem] leading-snug text-white/75">{label}</dt>
    </div>
  )
}

/* -------------------------------------------------------------------------
   Bagian fitur
   ------------------------------------------------------------------------- */

function MakeItYours() {
  const milestones = [
    { label: 'Audit data & rancangan', amount: 4_000_000, state: 'Dana cair', tone: 'text-brand', bar: 'bg-brand', width: '100%' },
    { label: 'Pembangunan dasbor', amount: 6_000_000, state: 'Menunggu tinjauan', tone: 'text-warning', bar: 'bg-warning', width: '62%' },
    { label: 'Serah terima', amount: 2_000_000, state: 'Dana terkunci', tone: 'text-subtle', bar: 'bg-line-strong', width: '18%' },
  ]

  return (
    <section className="border-y-2 border-line-strong bg-surface px-4 py-20 sm:px-6 lg:py-28">
      <div className="mx-auto grid w-full max-w-7xl items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <p className="mono-label text-[11px] text-brand">Escrow</p>
          <MaskedHeading className="display-light mt-4 text-ink" lines={['Uangnya diam dulu.']} />
          <p className="mt-5 max-w-md text-lg leading-relaxed text-muted">
            Klien menyetor sebelum pekerjaan dimulai. Dana berhenti di rekening penampungan dan baru berpindah
            saat hasilnya disetujui — atau saat mediator memutuskan pembagiannya.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              'Rincian potongan tampil sebelum Anda menyetujui apa pun',
              'Setiap perpindahan dana tercatat dan tidak dapat diubah',
              'Tinjauan yang lewat tenggat melepas dana otomatis',
            ].map((line) => (
              <li key={line} className="flex gap-3 text-muted">
                <Check className="mt-0.5 size-4.5 shrink-0 text-brand" />
                {line}
              </li>
            ))}
          </ul>

          <div className="mt-9">
            <Link to="/proyek">
              <Button size="lg" variant="inverted">
                Lihat papan proyek
                <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="card-hard bg-raised p-6">
            <div className="flex items-baseline justify-between gap-4">
              <p className="mono-label text-[11px] text-subtle">Kontrak escrow</p>
              <p className="mono-label text-[11px] text-brand">3 milestone</p>
            </div>
            <p className="mt-2 text-xl font-bold text-ink">Dasbor analitik penjualan</p>

            <div className="mt-6 space-y-3">
              {milestones.map((row) => (
                <div key={row.label} className="rounded-2xl border border-line p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-ink">{row.label}</p>
                      <p className={`mono-label mt-1 text-[11px] ${row.tone}`}>{row.state}</p>
                    </div>
                    <p className="shrink-0 font-bold tabular-nums text-ink">{formatRupiah(row.amount)}</p>
                  </div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-line">
                    <div className={`h-full rounded-full ${row.bar}`} style={{ width: row.width }} />
                  </div>
                </div>
              ))}
            </div>

            <dl className="mt-6 space-y-2 border-t border-line pt-5 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted">Nilai kotor</dt>
                <dd className="font-semibold tabular-nums text-ink">{formatRupiah(12_000_000)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">Komisi platform 10%</dt>
                <dd className="font-semibold tabular-nums text-danger">− {formatRupiah(1_200_000)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="font-bold text-ink">Diterima tenaga ahli</dt>
                <dd className="text-lg font-bold tabular-nums text-brand">{formatRupiah(10_800_000)}</dd>
              </div>
            </dl>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------
   Perusahaan
   ------------------------------------------------------------------------- */

function BringYourTeam() {
  const { data } = useVacancies({})
  const vacancies = data?.vacancies.slice(0, 6) ?? []

  const tiles = [
    { label: 'Kelas', className: 'border border-line text-ink' },
    { label: 'Jasa', className: 'border border-line text-ink' },
    { label: 'Proyek', className: 'border border-line text-ink' },
    { label: 'Sayembara', className: 'border border-line text-ink' },
    { label: 'Lowongan', className: 'border border-line text-ink' },
    { label: 'Talenta', className: 'border border-line text-ink' },
    { label: 'Escrow', className: 'border border-line text-ink' },
    { label: 'Mediasi', className: 'border border-line text-ink' },
    { label: 'Dompet', className: 'border border-line text-ink' },
  ]

  return (
    <section className="bg-sunken px-4 py-20 sm:px-6 lg:py-28">
      <div className="mx-auto grid w-full max-w-7xl items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <p className="mono-label text-[11px] text-lowongan">Perusahaan</p>
          <MaskedHeading className="display-light mt-4 text-ink" lines={['Bawa satu perusahaan.']} />
          <p className="mt-5 max-w-md text-lg leading-relaxed text-muted">
            Buat profil perusahaan yang dapat diverifikasi, pasang lowongan, dan pantau setiap pelamar dari satu
            papan. Perekrutan tidak memakai escrow — yang kami jaga di sana adalah kejelasan prosesnya.
          </p>

          {vacancies.length > 0 ? (
            <div className="mt-8 space-y-2">
              {vacancies.slice(0, 3).map((vacancy) => (
                <Link
                  key={vacancy.id}
                  to="/lowongan/$vacancyId"
                  params={{ vacancyId: vacancy.id }}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-line px-4 py-3 transition hover:border-line-strong"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ink">{vacancy.title}</p>
                    <p className="mono-label mt-0.5 truncate text-[11px] text-subtle">
                      {vacancy.company.name} · {vacancy.city || 'Jarak jauh'}
                    </p>
                  </div>
                  {vacancy.salary_visible && vacancy.salary_max ? (
                    <p className="mono-label shrink-0 text-[11px] text-lowongan">
                      {formatRupiahShort(vacancy.salary_max)}
                    </p>
                  ) : null}
                </Link>
              ))}
            </div>
          ) : null}

          <div className="mt-9">
            <Link to="/lowongan">
              <Button size="lg" variant="inverted">
                Lihat lowongan
                <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </Reveal>

        <Reveal delay={120} className="grid grid-cols-3 gap-4">
          {tiles.map((tile, index) => (
            <div
              key={tile.label}
              className={`card-hard tilt flex aspect-square items-center justify-center p-3 text-center ${tile.className}`}
              style={{ ['--tilt' as string]: `${(index % 3) - 1}deg` }}
            >
              <span className="mono-label text-[11px]">{tile.label}</span>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------
   Pita kuning
   ------------------------------------------------------------------------- */

function EscrowBand() {
  const steps = [
    { n: '01', title: 'Sepakati lingkup', copy: 'Pesan paket jasa, terima proposal, atau buka sayembara.' },
    { n: '02', title: 'Dana dikunci', copy: 'Klien menyetor sebelum pekerjaan dimulai.' },
    { n: '03', title: 'Hasil ditinjau', copy: 'Diajukan bertahap, revisi mengikuti jatah kontrak.' },
    { n: '04', title: 'Dana dilepas', copy: 'Disetujui, lewat tenggat, atau diputus mediator.' },
  ]

  return (
    <section className="relative isolate overflow-hidden bg-surface px-4 py-20 sm:px-6 lg:py-28">
      <div aria-hidden className="grid-lines pointer-events-none absolute inset-0" />

      <div className="relative mx-auto w-full max-w-7xl">
        <Reveal>
          <p className="mono-label text-[11px] text-subtle">Cara kerjanya</p>
          <MaskedHeading
            className="display-sm mt-4 max-w-2xl text-ink"
            lines={['Empat langkah yang sama', 'untuk pekerjaan sekecil apa pun.']}
          />
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => (
            <Reveal key={step.n} delay={index * 80}>
              <div className="card-hard h-full bg-white p-6">
                <p className="mono-label text-[11px] text-subtle">{step.n}</p>
                <h3 className="mt-4 text-xl font-bold text-ink">{step.title}</h3>
                <p className="mt-2 leading-relaxed text-muted">{step.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------
   Tiga peran
   ------------------------------------------------------------------------- */

function Plans() {
  const { data } = useContests({ status: 'open' })
  const prize = data?.contests[0]?.prize_amount

  const plans = [
    {
      tag: 'Peserta',
      title: 'Gratis',
      note: '',
      copy: 'Ikuti kelas, kumpulkan sertifikat, dan lamar pekerjaan.',
      features: ['Akun dan profil', 'Katalog kelas penuh', 'Melamar lowongan', 'Ikut sayembara'],
      to: '/daftar' as const,
      cta: 'Daftar',
      shell: 'bg-raised text-ink',
      button: 'bg-inverted text-on-inverted',
      muted: 'text-muted',
    },
    {
      tag: 'Tenaga ahli',
      title: '10%',
      note: 'per pekerjaan',
      copy: 'Jual jasa, ambil proyek, dan cairkan saldo ke rekening.',
      features: ['Paket jasa dan proposal', 'Escrow bermilestone', 'Profil talenta', 'Pencairan dengan verifikasi'],
      to: '/jasa' as const,
      cta: 'Mulai jual jasa',
      shell: 'bg-arang-900 text-white',
      button: 'bg-white text-black',
      muted: 'text-white/75',
    },
    {
      tag: 'Perusahaan',
      title: 'Rp 0',
      note: 'pasang lowongan',
      copy: 'Rekrut tanpa biaya platform, bayar hanya untuk pekerjaan berescrow.',
      features: ['Profil terverifikasi', 'Papan pelamar', 'Pesan paket jasa', prize ? `Sayembara hadiah ${formatRupiahShort(prize)}` : 'Buka sayembara'],
      to: '/app/perusahaan' as const,
      cta: 'Buat profil',
      shell: 'bg-sunken text-ink',
      button: 'bg-inverted text-on-inverted',
      muted: 'text-muted',
    },
  ]

  return (
    <section className="bg-surface px-4 py-20 sm:px-6 lg:py-28">
      <Reveal className="mx-auto max-w-2xl text-center">
        <MaskedHeading
          className="display-light text-ink"
          lines={['Bayar hanya saat', 'ada uang berpindah.']}
        />
        <p className="mt-5 text-lg leading-relaxed text-muted">
          Membuat akun, memasang lowongan, dan menelusuri katalog tidak dipungut biaya. Potongan hanya muncul
          ketika sebuah pekerjaan benar-benar dibayar.
        </p>
      </Reveal>

      <div className="mx-auto mt-14 grid w-full max-w-6xl gap-6 lg:grid-cols-3">
        {plans.map((plan, index) => (
          <Reveal key={plan.tag} delay={index * 90}>
            <div className={`flex h-full flex-col rounded-[2rem] p-8 ${plan.shell}`}>
              <p className={`mono-label text-[11px] ${plan.muted}`}>{plan.tag}</p>

              <p className="mt-5 flex items-baseline gap-2">
                <span className="text-5xl font-bold tracking-[-0.04em]">{plan.title}</span>
                {plan.note ? <span className={`text-sm ${plan.muted}`}>{plan.note}</span> : null}
              </p>

              <p className={`mt-4 leading-relaxed ${plan.muted}`}>{plan.copy}</p>

              <ul className="mt-7 flex-1 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex gap-2.5">
                    <Check className="mt-0.5 size-4.5 shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to={plan.to} className="mt-8">
                <Button size="lg" fullWidth className={plan.button}>
                  {plan.cta}
                </Button>
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------
   Ajakan penutup
   ------------------------------------------------------------------------- */

function ClosingCta() {
  return (
    <section className="relative isolate overflow-hidden border-t border-line bg-page px-4 py-24 text-center sm:px-6">
      <div aria-hidden className="grid-lines pointer-events-none absolute inset-0" />

      <div className="relative mx-auto max-w-2xl">
        <p className="mono-label text-[11px] text-subtle">Mulai hari ini</p>
        <MaskedHeading
          className="display-sm mt-5 text-ink"
          lines={['Tempat orang yang', 'benar-benar mengerjakan', 'sesuatu — dan peduli', 'bagaimana ia dibayar.']}
        />
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/daftar">
            <Button size="lg" variant="inverted">
              Buat akun gratis
              <ArrowRight className="size-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  )
}
